import { debugLog } from '~/debug';
import type { IIframeInterface } from '~/modules/iframes/Iframe';
import { Listener } from './Listener';
import { getColorLuminance } from '~/utils/color';
import TokenHandler from '~/modules/TokenHandler';
import Session from '~/modules/Session';
import { safeLocalStorageGet, safeLocalStorageSetDelete, safeLocalStorageSetForDuration } from '~/utils/localStorage';
import type { ModalListener } from './ModalListener';
import { ModalWindowListener } from './ModalWindowListener';
import ModalWindow from '~/modules/iframes/ModalWindow';
import type { IArticleConfig } from '~/config/article';
import type { CommentsListener } from './CommentsListener';
import type WidgetsCreator from '~/modules/WidgetsCreator';
import CommunicationCreator from '../index';
import QuizCallToActionPopup from '~/modules/QuizCallToActionPopup';
import NotificationAlert from '~/modules/NotificationAlert';
import ServerLogger from '~/modules/ServerLogger';
import type { ILoggerAction } from '~/modules/ServerLogger';
import type { EmotesListener } from './EmotesListener';
import type { PowerbarListener } from './PowerbarListener';
import type { IAdvancedConfig } from '~/modules/Advertisement/types';
import { isInCrossOriginFrame, isInIframe } from '~/utils/dom';
import type { IframelessCommentsListener } from './IframelessCommentsListener';
import SecureFormHandler from './Helpers/SecureFormHandler';
import type QuizIframe from '~/modules/iframes/Quiz';
import WidgetsConfig from '~/config/widgets.config';

type FrequencyCapObject = {
  firstSeen: number;
  lastSeen: number;
  frequency: number;
};

export class QuizListener extends Listener {
  private userFormDataHandler = new SecureFormHandler(this.articleConfig.apiKey);
  private static marketResearchQuestionAnswers: Record<string, boolean> = {};
  private modalWindow: ModalWindow;
  private modalWindowListener: ModalWindowListener;
  private modalIframe: IIframeInterface;
  private commentsListener: CommentsListener;
  private iframelessCommentsListener: IframelessCommentsListener;
  private ctaPopup: QuizCallToActionPopup;
  private loginTypes: string[] = [];
  private themeColor = '';
  private customLogoLink = '';
  private formerHeight = 275;
  private audienceData: {
    networkCode: string;
    audienceSegments: {
      topic: string;
      segment_id: string;
    }[];
  };
  private fireFirstPartyDataPixel = false;
  private heatMapAnalysisScriptInserted = false;
  private heatMapAnalysisEnabled = false;
  private hasSpaceForSurveyAds = true;
  constructor(
    protected widgetObject: IIframeInterface,
    private modalListener: ModalListener | undefined,
    private readonly articleConfig: IArticleConfig,
    private readonly index: string,
    private readonly widgetsCreator: WidgetsCreator | undefined,
    private readonly emotesListener: EmotesListener | undefined,
    private readonly powerbarListeners: PowerbarListener[] = []
  ) {
    super(widgetObject);
    window.addEventListener('message', this.listener);
    if (articleConfig.amp) {
      this.modalWindow = new ModalWindow(articleConfig);
    }
    this.postMessage({ canUseCookies: Session.canUseCookies });
    /** Send user token */
    this.postMessage({ loginToken: TokenHandler.getToken() });
    TokenHandler.addSSOTokenListener(`${widgetObject.id}-listener`, (token) => {
      this.postMessage({ loginToken: token });
    });
    TokenHandler.addSSOLogoutListener(`${widgetObject.id}-listener`, () => {
      this.postMessage({ loginToken: null });
    });
    let themeChangeAttempts = 50;
    const widgetThemeInterval = setInterval(() => {
      if (themeChangeAttempts === 0) {
        clearInterval(widgetThemeInterval);
        return;
      }
      const widgetContainer = widgetObject.iframe.parentElement;
      if (widgetContainer) {
        this.postMessage({ shouldUseDarkMode: getColorLuminance(widgetContainer) < 0.3 });
        clearInterval(widgetThemeInterval);
      }
      themeChangeAttempts--;
    }, 100);
    if (Session.userCountryDetermined) {
      const cookieUsageAgreed = safeLocalStorageGet('vuukle_cookie_usage_agreed') === 'true';
      import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
        if (consentManager.default.isGDPR && typeof window.__tcfapi === 'undefined') {
          if (cookieUsageAgreed) {
            Session.canUseCookies = true;
            consentManager.default.hasConsent = true;
          } else {
            this.postMessage({ showCookieNotice: true });
          }
        }
        if (consentManager.default.isGDPR && typeof window.__tcfapi !== 'undefined') {
          consentManager.default.addVendorPresenceListener((isPresent) => {
            if (!isPresent) {
              this.postMessage({ showCookieNotice: true });
            }
          });
        }
      });
    } else {
      const interval = setInterval(() => {
        if (Session.userCountryDetermined) {
          import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
            const cookieUsageAgreed = safeLocalStorageGet('vuukle_cookie_usage_agreed') === 'true';
            if (consentManager.default.isGDPR && typeof window.__tcfapi === 'undefined') {
              if (cookieUsageAgreed) {
                Session.canUseCookies = true;
                consentManager.default.hasConsent = true;
              } else {
                this.postMessage({ showCookieNotice: true });
              }
            }
            if (consentManager.default.isGDPR && typeof window.__tcfapi !== 'undefined') {
              consentManager.default.addVendorPresenceListener((isPresent) => {
                if (!isPresent) {
                  this.postMessage({ showCookieNotice: true });
                }
              });
            }
          });
          clearInterval(interval);
        }
      }, 1000);
    }
    this.postMessage({ userId: safeLocalStorageGet('vuukle_user_id') });
    const responseGroups = safeLocalStorageGet('quizzly_surveys_response_groups');
    if (responseGroups) {
      this.postMessage({ responseGroups });
    }

    if (typeof window.getQuizzlyMarketResearchQuestionAnswers !== 'function') {
      window.getQuizzlyMarketResearchQuestionAnswers = this.getMarketResearchQuestionAnswers.bind(this);
    }

    const isPlatformInsideIframe = isInIframe() || isInCrossOriginFrame();
    const isWideEnough = window.innerWidth >= 600;
    const isHighEnough = window.innerHeight >= 600;

    const isThereSpaceForSurveyAds = isPlatformInsideIframe ? isHighEnough || isWideEnough : true;
    this.hasSpaceForSurveyAds = isThereSpaceForSurveyAds;

    // If there isn't enough space to show a GAM tag in the survey,
    // Quiz widget will force responses to be part of the control group instead of the exposed group
    if (isPlatformInsideIframe && !isThereSpaceForSurveyAds && !articleConfig.quiz.forceExposedGroup) {
      this.postMessage({ forceControlGroup: true });
    }
  }

  private getMarketResearchQuestionAnswers = () => QuizListener.marketResearchQuestionAnswers;

  public async enableCTAPopup(ctaPopupConfig: IAdvancedConfig['quizCallToAction']) {
    const quizCTAPopupModule = await import(/* webpackChunkName: "qcp" */ '~/modules/QuizCallToActionPopup');
    this.ctaPopup = new quizCTAPopupModule.default(ctaPopupConfig, this);
    this.postMessage({
      enableCTAButtonWithText: ctaPopupConfig?.callToActionText,
    });
  }

  protected listener = (event: any): void => {
    if (!this.isValidEvent(event)) {
      return;
    }

    debugLog('log', '[Vuukle.js | Quiz] message:', event);

    // Set login types
    if (event.data.loginTypes) {
      this.loginTypes = event.data.loginTypes;
    }

    if (event.data.getQuestionAnswer) {
      this.postMessage({ chosenAnswer: this.getQuestionAnswer(event.data.getQuestionAnswer) });
    }

    if (event.data.getRecordedIdOfReportedQuestion) {
      this.postMessage({ recordedQuestionId: this.getReportedQuestion(event.data.getRecordedIdOfReportedQuestion) });
    }

    if (event.data.setReportedQuestion) {
      const { quizId, reportId } = event.data.setReportedQuestion;
      this.setReportedQuestion(quizId, reportId);
    }

    if (event.data.setQuestionAnswer) {
      const { quizId, answer } = event.data.setQuestionAnswer;
      this.setQuestionAnswer(quizId, answer);
      if (window.gtag && CommunicationCreator.sendGAEvents) {
        window.gtag('event', this.articleConfig.overrideGAEventNames?.['quizzly_click'] || 'quizzly_click', {
          event_category: 'vuukle_engagement',
        });
      }
    }

    if (typeof event.data.userAge !== 'undefined' && typeof event.data.surveyId === 'string') {
      this.setDemographicQuestionAge(event.data.surveyId, event.data.userAge);
    }

    if (typeof event.data.userGender !== 'undefined' && typeof event.data.surveyId === 'string') {
      this.setDemographicQuestionGender(event.data.surveyId, event.data.userGender);
    }

    /** Quiz requests for Modal login page to be opened, usually for quiz scores */
    if ('requestLogin' in event.data) {
      this.handleLoginRequest(event);
    }

    /** Quiz log out*/
    if (event.data.loggedOutQW) {
      TokenHandler.removeToken();
      this.commentsListener?.postMessage({ loggedOutQW: true });
      this.iframelessCommentsListener?.postMessage({ loggedOutQW: true });
      this.modalListener?.postMessage({ removeSLToken: true });
      this.powerbarListeners.forEach((powerbar) => {
        powerbar.postMessage({ loginToken: null });
      });
      this.emotesListener?.postMessage({ loginToken: null });
      if (TokenHandler.isSSO) {
        const logoutFunction =
          this.articleConfig.auth?.sso?.onLogout || this.articleConfig.comments.auth?.sso?.onLogout;
        if (logoutFunction) {
          logoutFunction();
        }
      }
    }

    if (event.data.callSSO) {
      this.performSSOAuth();
    }

    if (event.data.removeAnswersForTheseIds) {
      this.removeAnswersForIDs(event.data.removeAnswersForTheseIds);
    }

    if (event.data.showCTAPopup) {
      if (typeof window.quizCallToActionCallback === 'function') {
        window.quizCallToActionCallback(this, this.ctaPopup);
      } else {
        this.ctaPopup.initializePopup();
        this.ctaPopup.showPopup();
      }
    }

    // Open Browser link
    if (event.data.openLink) {
      window.location.href = event.data.openLink;
    }

    if (event.data.openLinkInNewTab) {
      // window.open(event.data.openLinkInNewTab, '_blank');
      if (event.data.shouldOpenInNewTab) {
        const anchorTag = document.createElement('a');
        anchorTag.href = event.data.openLinkInNewTab;
        anchorTag.target = '_blank';
        anchorTag.rel = 'nofollow noopener';
        anchorTag.click();
      } else {
        this.openLinkInModal(event.data.openLinkInNewTab);
      }
    }

    if (event.data.showRecommendedQuizzes) {
      this.openModalRecommendedQuizzes();
    }

    /** The actual message we send doesn't matter, the purpose of this is to load modal */
    if (event.data.preloadModal) {
      this.sendMessageToModal({ aMessage: 'msg' });
    }

    // Show an alert message
    if (event.data.alertMessage) {
      NotificationAlert.createNotification(event.data.alertMessage.text, event.data.alertMessage.type);
    }

    // Report BQ and GA events when user shares the article through Quiz
    if (event.data.articleSharedWithNetwork) {
      ServerLogger.log(`share_${event.data.articleSharedWithNetwork}` as ILoggerAction, this.articleConfig);
      // if (window.gtag && CommunicationCreator.sendGAEvents) {
      //   window.gtag(
      //     'event',
      //     this.articleConfig.overrideGAEventNames?.[`share_${event.data.articleSharedWithNetwork}`] ||
      //       `share_${event.data.articleSharedWithNetwork}`,
      //     {
      //       event_category: 'vuukle_engagement',
      //     }
      //   );
      // }
    }

    // Custom publisher logo link may be used in the Modal login screen for example
    if (event.data.customLogoLink) {
      this.customLogoLink = event.data.customLogoLink;
    }

    if (event.data.cookieUsageAgreed) {
      import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_cookie_usage_agreed', () => {
          safeLocalStorageSetDelete('vuukle_cookie_usage_agreed', 'true');
        });
        consentManager.default.hasConsent = true;
        consentManager.default.cookieConsentKnown = true;
      });
      Session.canUseCookies = true;
      this.commentsListener?.postMessage({ cookieNoticeConfirmed: true });
    }

    if (event.data.questionToUpdateFrequencyFor) {
      this.updateQuestionFrequency(event.data.questionToUpdateFrequencyFor);
    }

    if (event.data.userId) {
      this.setUserID(event.data.userId);
    }

    if (event.data.removeUserId) {
      safeLocalStorageSetDelete('vuukle_user_id', null);
    }

    if (event.data.closeCommentsModal) {
      this.commentsListener?.postMessage({ closeCommentsModal: true });
      this.iframelessCommentsListener?.postMessage({ closeCommentsModal: true });
    }

    if (
      typeof event.data.marketResearchQuestionAnswer === 'object' &&
      event.data.marketResearchQuestionAnswer !== null &&
      'answer' in event.data.marketResearchQuestionAnswer &&
      'topic' in event.data.marketResearchQuestionAnswer
    ) {
      const topicWithDashes = event.data.marketResearchQuestionAnswer.topic.replace(/\s+/g, '-');
      QuizListener.marketResearchQuestionAnswers[topicWithDashes] = event.data.marketResearchQuestionAnswer.answer;
      /** Call market research question event listener with the current market research question answers object */
      if (typeof this.articleConfig.quiz.onMarketResearchQuestionAnswered === 'function') {
        this.articleConfig.quiz.onMarketResearchQuestionAnswered(QuizListener.marketResearchQuestionAnswers);
      }
    }

    if (event.data.targetingTopic) {
      const windowContext = isInIframe() && !isInCrossOriginFrame() && window.top ? window.top : window;
      // For topics that have multiple words, we need to use dashes (-) instead of spaces
      const topicWithDashes = event.data.targetingTopic.replace(/\s+/g, '-');
      if (this.fireFirstPartyDataPixel) {
        const topicId = this.audienceData.audienceSegments.find((value) => value.topic === topicWithDashes)?.segment_id;
        if (topicId) {
          const pixelTagScript = windowContext.document.createElement('script');
          pixelTagScript.async = true;
          pixelTagScript.id = 'google-pcd-tag';
          pixelTagScript.src = 'https://pagead2.googlesyndication.com/pagead/js/pcd.js';
          pixelTagScript.dataset.audiencePixel = `dc_iu=/${this.audienceData.networkCode}/DFPAudiencePixel;dc_seg=${topicId};quizzly_audience=${topicWithDashes}`;
          windowContext.document.body.appendChild(pixelTagScript);
        }
      } else {
        windowContext.googletag = windowContext.googletag || {};
        windowContext.googletag.cmd = windowContext.googletag.cmd || [];
        windowContext.googletag.cmd.push(() => {
          windowContext.googletag.pubads().setTargeting('quizzly_audience', topicWithDashes);
        });
      }

      if (event.data.removeUserData) {
        this.deleteCookies();
      }
    }

    if (event.data.insertHeatMapAnalysisScript) {
      this.insertHeatmapAnalysisScript();
    }

    if (event.data.questionToRemoveFromFreqCapObject) {
      this.removeQuestionFromFrequencyCapObject(event.data.questionToRemoveFromFreqCapObject);
    }

    if (event.data.removeMaxHeightLimit) {
      this.widgetObject.iframe.style.removeProperty('max-height');
    }

    if (event.data.requestUserData) {
      this.requestUserDataForm();
    }

    if (event.data.removeUserFormData) {
      this.userFormDataHandler.deleteData();
    }

    if (event.data.expandQuizForAd) {
      if (this.hasSpaceForSurveyAds) {
        this.postMessage({ setOnlyMaxHeightTo: 550 });
        (this.widgetObject as QuizIframe).changeHeight(550);
      }
    }

    if (event.data.shrinkQuizToFormerSize) {
      this.postMessage({ setOnlyMaxHeightTo: this.formerHeight === 275 ? 265 : this.formerHeight });
      (this.widgetObject as QuizIframe).changeHeight(this.formerHeight);
    }

    if (event.data.surveyIdToAddInLocalStorage) {
      this.saveSurveyIdInLocalStorage(event.data.surveyIdToAddInLocalStorage);
    }

    if (typeof event.data.showQuizAd === 'boolean') {
      let ad = document.querySelector<HTMLElement>(
        `${WidgetsConfig.selectors.quiz.concat(this.index)} + div[id*="vuukle-ad"]`
      );
      if (!ad) {
        ad = document.querySelector<HTMLElement>(
          `${WidgetsConfig.selectors.quizAlt.concat(this.index)} + div[id*="vuukle-ad"]`
        );
      }

      if (event.data.showQuizAd) {
        if (ad) {
          ad.style.display = 'block';
        }
        (this.widgetObject as QuizIframe).changeWidth('100%');
      }

      if (!event.data.showQuizAd && ad) {
        if (ad) {
          ad.style.display = 'none';
        }
        if (window.innerWidth >= 610) {
          (this.widgetObject as QuizIframe).changeWidth('610px');
        }
      }
    }
  };

  private saveSurveyIdInLocalStorage = (surveyId: string) => {
    import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
      const currentSurveyIds = safeLocalStorageGet('quizzly_surveys_response_groups');
      if (!currentSurveyIds) {
        const surveyIds = [surveyId];
        consentManager.default.executeWhenStorageValueCanBeSet('quizzly_surveys_response_groups', () => {
          safeLocalStorageSetForDuration('quizzly_surveys_response_groups', JSON.stringify(surveyIds), 7 * 24);
        });
      } else {
        const surveyIds: string[] = JSON.parse(currentSurveyIds);
        if (!currentSurveyIds.includes(surveyId)) {
          surveyIds.push(surveyId);
        }
        consentManager.default.executeWhenStorageValueCanBeSet('quizzly_surveys_response_groups', () => {
          safeLocalStorageSetForDuration('quizzly_surveys_response_groups', JSON.stringify(surveyIds), 7 * 24);
        });
      }
    });
  };

  /**
   * Delete all cookies set by Quiz and other widgets
   */
  private deleteCookies() {
    Session.deleteCookies();

    if (this.modalListener) {
      this.modalListener.postMessage({ deleteCookies: true });
      this.modalListener.postMessage({ removeSLToken: true });
    }

    if (this.emotesListener) {
      this.emotesListener.removeCookies();
      this.emotesListener.postMessage({ loginToken: null });
    }

    this.powerbarListeners.forEach((powerbarListener) => {
      powerbarListener.postMessage({ deleteCookies: true });
      powerbarListener.postMessage({ loginToken: null });
      powerbarListener.removeCookies();
    });

    if (this.commentsListener) {
      this.commentsListener.postMessage({ deleteCookies: true });
      this.commentsListener.postMessage({ loggedOutQW: true });
    }

    if (this.iframelessCommentsListener) {
      this.iframelessCommentsListener.postMessage({ deleteCookies: true });
      this.iframelessCommentsListener.postMessage({ loggedOutQW: true });
    }
  }

  public enableHeatMapAnalysis() {
    this.heatMapAnalysisEnabled = true;
  }

  public insertHeatmapAnalysisScript() {
    if (!this.heatMapAnalysisScriptInserted && this.heatMapAnalysisEnabled) {
      this.heatMapAnalysisScriptInserted = true;
      const scriptElem = document.createElement('script');
      scriptElem.type = 'text/javascript';
      scriptElem.async = true;
      scriptElem.innerHTML = `
        (function(h,o,t,j,a,r){
          h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
          h._hjSettings={hjid:5095721,hjsv:6};
          a=o.getElementsByTagName('head')[0];
          r=o.createElement('script');r.async=1;
          r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
          a.appendChild(r);
        })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
      `;
      document.body.appendChild(scriptElem);
    }
  }

  public setThemeColor(color: string) {
    this.themeColor = color;
  }

  private setUserID(userId: string) {
    Session.userAPIKey = userId;
    if (Session.canUseCookies) {
      import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_user_id', () => {
          safeLocalStorageSetDelete('vuukle_user_id', userId);
        });
      });
    } else {
      Session.addStorageUpdateCallback(() => {
        import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
          consentManager.default.executeWhenStorageValueCanBeSet('vuukle_user_id', () => {
            safeLocalStorageSetDelete('vuukle_user_id', userId);
          });
        });
      });
    }
  }

  private removeAnswersForIDs(ids: string[]) {
    const quizAnswers = safeLocalStorageGet('vuukle_quiz_answers');
    if (quizAnswers) {
      const parsedAnswers = JSON.parse(quizAnswers);
      ids.forEach((id) => {
        if (typeof parsedAnswers[id] !== 'undefined' && parsedAnswers[id] !== null) {
          delete parsedAnswers[id];
        }
      });
      if (ids.includes('age') || ids.includes('gender')) {
        delete parsedAnswers['demographicQuestionAnswers'];
      }
      import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_quiz_answers', () => {
          safeLocalStorageSetDelete('vuukle_quiz_answers', JSON.stringify(parsedAnswers));
        });
      });
    }
  }

  private openAndInitializeWindowedModal() {
    this.modalWindow = new ModalWindow(this.articleConfig);
    this.modalWindow.openModalInWindow();
    this.modalWindowListener = new ModalWindowListener(this.modalWindow, this.articleConfig, this);
  }

  private async handleLoginRequest(event: any) {
    if (this.articleConfig.amp) {
      this.openAndInitializeWindowedModal();
      if (event.data.loginTypes) {
        this.loginTypes = event.data.loginTypes;
      }
      this.modalWindowListener.postMessage({
        loadModal: true,
        modalData: {
          type: 'login',
          data: { showQuizzlyLogo: true, loginFrom: 'quiz', customLogoLink: this.customLogoLink },
        },
        loginTypes:
          this.articleConfig.amp && this.articleConfig.ampLoginTypes
            ? this.articleConfig.ampLoginTypes
            : event.data.loginTypes || this.loginTypes,
      });
      this.modalWindowListener.changeThemeColor(this.themeColor);
    } else {
      if (event.data.loginTypes || (this.articleConfig.amp && this.articleConfig.ampLoginTypes)) {
        await this.sendMessageToModal({
          loadModal: true,
          loginTypes:
            this.articleConfig.amp && this.articleConfig.ampLoginTypes
              ? this.articleConfig.ampLoginTypes
              : event.data.loginTypes,
        });
        this.loginTypes = event.data.loginTypes;
      } else {
        await this.sendMessageToModal({
          modalData: {
            type: 'login',
            data: { showQuizzlyLogo: true, loginFrom: 'quiz', customLogoLink: this.customLogoLink },
          },
          loginTypes:
            this.articleConfig.amp && this.articleConfig.ampLoginTypes
              ? this.articleConfig.ampLoginTypes
              : this.loginTypes,
        });
      }
      this.toggleModal(true);
    }
  }

  private async requestUserDataForm() {
    try {
      const userData = this.userFormDataHandler.retrieveUserData();
      if (this.articleConfig.amp) {
        this.openAndInitializeWindowedModal();
        this.modalWindowListener.postMessage({
          loadModal: true,
          modalData: {
            data: userData || {},
            type: 'userInfo',
          },
        });
        this.modalWindowListener.changeThemeColor(this.themeColor);
      } else {
        await this.sendMessageToModal({
          modalData: {
            data: userData || {},
            type: 'userInfo',
          },
        });
        this.toggleModal(true);
      }
    } catch (_e) {
      if (this.articleConfig.amp) {
        this.openAndInitializeWindowedModal();
        this.modalWindowListener.postMessage({
          loadModal: true,
          modalData: {
            type: 'userInfo',
          },
        });
        this.modalWindowListener.changeThemeColor(this.themeColor);
      } else {
        await this.sendMessageToModal({
          modalData: {
            type: 'userInfo',
          },
        });
        this.toggleModal(true);
      }
    }
  }

  private performSSOAuth() {
    /** SSO methods */
    const ssoAuth = this.articleConfig.comments?.auth?.sso?.onClick || this.articleConfig.auth?.sso?.onClick;
    /** Call auth or show warning that it's not configured */
    typeof ssoAuth === 'function'
      ? ssoAuth()
      : console.error(`[Vuukle] ⚠ Please configure SSO to allow users to authenticate.`);
  }

  private async openLinkInModal(link: string) {
    await this.sendMessageToModal({ modalData: { type: 'iframeLink', data: { pageLink: link } } });
    this.toggleModal(true);
  }

  private async openModalRecommendedQuizzes() {
    if (this.articleConfig.amp) {
      this.openAndInitializeWindowedModal();
      this.modalWindowListener.postMessage({
        loadModal: true,
        modalData: { type: 'recommendedQuizzes', data: {} },
      });
      this.modalWindowListener.changeThemeColor(this.themeColor);
    } else {
      await this.sendMessageToModal({ modalData: { type: 'recommendedQuizzes', data: {} } });
      this.toggleModal(true);
    }
  }

  private async sendMessageToModal(data: any) {
    if (this.modalListener) {
      this.modalListener.postMessage(data);
    } else {
      // lazy load modal
      if (this.widgetsCreator) {
        await this.widgetsCreator.createModal(data);
        const widgetContainer = this.widgetObject.iframe.parentElement;
        (this.modalListener as unknown as ModalListener)?.switchThemeToDarkMode(
          getColorLuminance(widgetContainer) < 0.3
        );
        (this.modalListener as unknown as ModalListener)?.changeThemeColor(this.themeColor);
        this.commentsListener?.postMessage({ modalLoaded: true });
      }
    }
  }

  /**
   * Updates the modal iframe instance to be used by Powerbar listener
   * @param modalIframe
   * @param modalListener
   */
  public updateModalInstance(modalIframe: IIframeInterface, modalListener: ModalListener) {
    this.modalIframe = modalIframe;
    this.modalListener = modalListener;
  }

  public updateCommentsListenerInstance(commentsListener: CommentsListener) {
    this.commentsListener = commentsListener;
  }

  public updateIframelessCommentsInstance(commentsListener: IframelessCommentsListener) {
    this.iframelessCommentsListener = commentsListener;
  }

  public setFormerHeight(height: number) {
    this.formerHeight = height;
  }

  private toggleModal(show: boolean) {
    const modalNode: HTMLElement | null = document.querySelector(`#${this.modalIframe?.id}`);
    if (modalNode) {
      modalNode.style.display = show ? 'block' : 'none';
    }
  }

  private removeQuestionFromFrequencyCapObject(questionId: string) {
    import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
      const freqCaps = safeLocalStorageGet('vuukle_quiz_frequency_cap');
      if (freqCaps) {
        const parsedFreqCaps = JSON.parse(freqCaps);
        const question = parsedFreqCaps[questionId];
        if (question) {
          delete parsedFreqCaps[questionId];
          consentManager.default.executeWhenStorageValueCanBeSet('vuukle_quiz_frequency_cap', () => {
            safeLocalStorageSetDelete('vuukle_quiz_frequency_cap', JSON.stringify(parsedFreqCaps));
          });
        }
      }
    });
  }

  /**
   * Update the number of times the question was seen.
   * Usually used for sponsored questions to limit the times they're shown
   * @param questionId Question ID
   */
  private updateQuestionFrequency(questionId: string) {
    const currentFrequencyCaps = safeLocalStorageGet('vuukle_quiz_frequency_cap');
    if (currentFrequencyCaps) {
      const parsedFrequencyCaps: Record<string, FrequencyCapObject> = JSON.parse(currentFrequencyCaps);
      let frequencyCapQuestionObject = parsedFrequencyCaps[questionId];
      if (frequencyCapQuestionObject) {
        frequencyCapQuestionObject.frequency += 1;
        frequencyCapQuestionObject.lastSeen = Date.now();
      } else {
        frequencyCapQuestionObject = {
          frequency: 1,
          firstSeen: Date.now(),
          lastSeen: Date.now(),
        };
        parsedFrequencyCaps[questionId] = frequencyCapQuestionObject;
      }
      import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_quiz_frequency_cap', () => {
          safeLocalStorageSetDelete('vuukle_quiz_frequency_cap', JSON.stringify(parsedFrequencyCaps));
        });
      });
    } else {
      const frequencyCapObject: Record<string, FrequencyCapObject> = {};
      frequencyCapObject[questionId] = { frequency: 1, lastSeen: Date.now(), firstSeen: Date.now() };
      import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_quiz_frequency_cap', () => {
          safeLocalStorageSetDelete('vuukle_quiz_frequency_cap', JSON.stringify(frequencyCapObject));
        });
      });
    }
  }

  /**
   * Get the answer for a question from localStorage if the user has answered the quiz question already
   * @param questionId
   * @returns
   */
  private getQuestionAnswer(questionId: string) {
    const quizAnswers = safeLocalStorageGet('vuukle_quiz_answers');
    if (quizAnswers) {
      const parsedAnswers = JSON.parse(quizAnswers);
      return parsedAnswers[questionId];
    } else {
      return null;
    }
  }

  /**
   * Get the record id for the reported question if the user has reported questions
   * @param questionId
   * @returns
   */
  private getReportedQuestion(questionId: string) {
    const reportedQuestions = safeLocalStorageGet('vuukle_quiz_reported_questions');
    if (reportedQuestions) {
      const parsedQuestions = JSON.parse(reportedQuestions);
      return parsedQuestions[questionId];
    } else {
      return null;
    }
  }

  /**
   * Set demographic question's answered gender for the given survey
   * @param surveyId
   * @param gender
   */
  private setDemographicQuestionGender(surveyId: string, gender: string) {
    const setQuestionAnswerInner = () => {
      let quizAnswers = safeLocalStorageGet('vuukle_quiz_answers');

      import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
        if (!quizAnswers) {
          consentManager.default.executeWhenStorageValueCanBeSet('vuukle_quiz_answers', () => {
            safeLocalStorageSetDelete('vuukle_quiz_answers', '{}');
          });
          quizAnswers = safeLocalStorageGet('vuukle_quiz_answers');
        }

        const parsedAnswers = JSON.parse(quizAnswers || '{}');
        if (!parsedAnswers.demographicQuestionAnswers) {
          parsedAnswers.demographicQuestionAnswers = {};
        }
        if (!parsedAnswers.demographicQuestionAnswers[surveyId]) {
          parsedAnswers.demographicQuestionAnswers[surveyId] = {};
        }
        parsedAnswers.demographicQuestionAnswers[surveyId].gender = gender;
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_quiz_answers', () => {
          safeLocalStorageSetDelete('vuukle_quiz_answers', JSON.stringify(parsedAnswers));
        });
      });
    };

    if (Session.canUseCookies) {
      setQuestionAnswerInner();
    } else {
      Session.addStorageUpdateCallback(() => {
        setQuestionAnswerInner();
      });
    }
  }

  /**
   * Set demographic question's answered age for the given survey
   * @param surveyId
   * @param age
   */
  private setDemographicQuestionAge(surveyId: string, age: string) {
    const setQuestionAnswerInner = () => {
      let quizAnswers = safeLocalStorageGet('vuukle_quiz_answers');

      import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
        if (!quizAnswers) {
          consentManager.default.executeWhenStorageValueCanBeSet('vuukle_quiz_answers', () => {
            safeLocalStorageSetDelete('vuukle_quiz_answers', '{}');
          });
          quizAnswers = safeLocalStorageGet('vuukle_quiz_answers');
        }

        const parsedAnswers = JSON.parse(quizAnswers || '{}');
        if (!parsedAnswers.demographicQuestionAnswers) {
          parsedAnswers.demographicQuestionAnswers = {};
        }
        if (!parsedAnswers.demographicQuestionAnswers[surveyId]) {
          parsedAnswers.demographicQuestionAnswers[surveyId] = {};
        }
        parsedAnswers.demographicQuestionAnswers[surveyId].age = age;
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_quiz_answers', () => {
          safeLocalStorageSetDelete('vuukle_quiz_answers', JSON.stringify(parsedAnswers));
        });
      });
    };

    if (Session.canUseCookies) {
      setQuestionAnswerInner();
    } else {
      Session.addStorageUpdateCallback(() => {
        setQuestionAnswerInner();
      });
    }
  }

  /**
   * Set the user's answer inside localStorage
   * @param questionId
   * @param answerIndex
   * @param answer
   */
  private setQuestionAnswer(questionId: string, answer: number | string | object | number[] | string[] | object[]) {
    const setQuestionAnswerInner = () => {
      let quizAnswers = safeLocalStorageGet('vuukle_quiz_answers');

      import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
        if (!quizAnswers) {
          consentManager.default.executeWhenStorageValueCanBeSet('vuukle_quiz_answers', () => {
            safeLocalStorageSetDelete('vuukle_quiz_answers', '{}');
          });
          quizAnswers = safeLocalStorageGet('vuukle_quiz_answers');
        }

        const parsedAnswers = JSON.parse(quizAnswers || '{}');
        parsedAnswers[questionId] = answer;
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_quiz_answers', () => {
          safeLocalStorageSetDelete('vuukle_quiz_answers', JSON.stringify(parsedAnswers));
        });
      });
    };

    if (Session.canUseCookies) {
      setQuestionAnswerInner();
    } else {
      Session.addStorageUpdateCallback(() => {
        setQuestionAnswerInner();
      });
    }
  }

  /**
   * Set the recorded id of a reported question in localStorage
   * @param questionId
   * @param recordedId
   */
  private setReportedQuestion(questionId: string, recordedId: string) {
    const setReportedQuestionInner = () => {
      let quizAnswers = safeLocalStorageGet('vuukle_quiz_reported_questions');
      import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
        if (!quizAnswers) {
          consentManager.default.executeWhenStorageValueCanBeSet('vuukle_quiz_reported_questions', () => {
            safeLocalStorageSetDelete('vuukle_quiz_reported_questions', '{}');
          });

          quizAnswers = safeLocalStorageGet('vuukle_quiz_reported_questions');
        }

        const parsedAnswers = JSON.parse(quizAnswers || '{}');
        parsedAnswers[questionId] = recordedId;
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_quiz_reported_questions', () => {
          safeLocalStorageSetDelete('vuukle_quiz_reported_questions', JSON.stringify(parsedAnswers));
        });
      });
    };

    if (Session.canUseCookies) {
      setReportedQuestionInner();
    } else {
      Session.addStorageUpdateCallback(() => {
        setReportedQuestionInner();
      });
    }
  }

  /**
   * Track the time user has spent viewing the Quiz widget and report it to the BQ server
   */
  public trackTimeSpentOnWidget() {
    let startTime = 0;
    let hasFirstEnteredView = false;
    let isCurrentlyInViewport = false;
    let timestampReportedWhenThePageWasHidden = false;
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startTime = Math.floor(Date.now() / 1000);
            hasFirstEnteredView = true;
            isCurrentlyInViewport = true;
          } else {
            const endTime = Math.floor(Date.now() / 1000);
            isCurrentlyInViewport = false;
            if (hasFirstEnteredView && !timestampReportedWhenThePageWasHidden) {
              ServerLogger.log('quizzly_view_time', this.articleConfig, [
                { key: 'ViewingStartedTs', value: startTime },
                { key: 'ViewingEndedTs', value: endTime },
              ]);
            }
            timestampReportedWhenThePageWasHidden = false;
          }
        });
      },
      { threshold: 0.1 }
    );
    intersectionObserver.observe(this.widgetObject.iframe);
    // Handle the case where the user closes the page when Quiz is still in the viewport
    window.addEventListener('visibilitychange', () => {
      if (isCurrentlyInViewport && document.visibilityState === 'hidden') {
        timestampReportedWhenThePageWasHidden = true;
        ServerLogger.logPost('quizzly_view_time', this.articleConfig, true, [
          { key: 'ViewingStartedTs', value: startTime },
          { key: 'ViewingEndedTs', value: Math.floor(Date.now() / 1000) },
        ]);
      }
    });
  }

  /**
   * Change widget theme
   * @param theme Light or dark mode
   */
  public changeTheme(theme: 'light' | 'dark') {
    this.postMessage({ changeTheme: theme });
  }

  /**
   * Change from setting a targeting on a page to firing a pixel tag
   * @param networkCode
   * @param audienceData
   */
  public enableAudienceTrackingPixel(
    networkCode: string,
    audienceData: (typeof this.audienceData)['audienceSegments']
  ) {
    this.fireFirstPartyDataPixel = true;
    this.audienceData = { audienceSegments: audienceData, networkCode };
  }
}
